"use client"

import React, {type ForwardedRef, forwardRef, useEffect, useState, useRef} from "react";
import Tippy from "@tippyjs/react/headless";
import "tippy.js/dist/tippy.css";
import _ from "lodash";

import {cn, useMergeRefs} from "@isudio/utils";
import {type GenderType, GenderCaption} from "@isudio/data";

import {Input, Label, useTippyRegistry} from "../";

type InputProps =
  React.InputHTMLAttributes<HTMLInputElement>
  & {
  defaultValue?: GenderType;
  error?: string | undefined;
  onGenderChange?: (value: GenderType) => void;
  onButtonBlur?: () => void;
};

const genderOptions: {
  selectedValue: GenderType;
  displayValue: string;
  icon: string;
  activeColor: string;
  hoverColor: string;
}[] = [
  {
    selectedValue: "m",
    displayValue: GenderCaption.m,
    icon: "fa-mars",
    activeColor: "text-generic-blue",
    hoverColor: "hover:text-gray-500 dark:hover:text-white"
  },
  {
    selectedValue: "f",
    displayValue: GenderCaption.f,
    icon: "fa-venus",
    activeColor: "text-generic-pink",
    hoverColor: "hover:text-gray-500 dark:hover:text-white"
  },
  {
    selectedValue: "o",
    displayValue: GenderCaption.o,
    icon: "fa-transgender",
    activeColor: "text-generic-purple",
    hoverColor: "hover:text-gray-500 dark:hover:text-white"
  },
];

const GenderRadio = forwardRef<HTMLInputElement, InputProps>(
  (
    {
     className,
      defaultValue,
      error,
      onGenderChange,
      onFocus,
      onBlur,
      onButtonBlur,
      ...props
    },
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const {
      register,
      unregister
    } = useTippyRegistry();

    const [showError, setShowError] = useState(false);
    const [hovering, setHovering] = useState(false);
    const hideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const [selectedValue, setSelectedValue] = useState<GenderType>(defaultValue);
    const [displayValue, setDisplayValue] = useState<string | undefined>(_.find(genderOptions, {selectedValue: defaultValue})?.displayValue);
    const internalRef = useRef<HTMLInputElement | null>(null);
    const initialVal = props.value ?? defaultValue ?? (internalRef.current?.value ?? "");
    const [hasValue, setHasValue] = useState(Boolean(String(initialVal).trim()));

    useEffect(() => {
      const domVal = internalRef.current?.value;
      const v = props.value ?? defaultValue ?? domVal ?? "";
      setHasValue(Boolean(String(v).trim()));
    }, [props.value, defaultValue]);

    const clearHideTimer = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };

    const startHideTimer = (delay = 2000) => {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => {
        setShowError(false);
        hideTimerRef.current = null;
      }, delay);
    };

    useEffect(() => {
      if (error && !hovering) {
        setShowError(true);
        startHideTimer();
      } else if (!error) {
        setShowError(false);
        clearHideTimer();
      }
      return clearHideTimer;
    }, [error]);

    useEffect(() => {
      setSelectedValue(defaultValue);
      setDisplayValue(_.find(genderOptions, { selectedValue: defaultValue })?.displayValue);
      onGenderChange?.(defaultValue);
    }, [defaultValue]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.setSelectionRange(0, 0);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
      if (error) {
        setShowError(true);
        startHideTimer();
      }
    };

    const handleMouseEnter = () => {
      if (!error) return;
      clearHideTimer();
      setHovering(true);
      setShowError(true);
    };

    const handleMouseLeave = () => {
      setHovering(false);
      setShowError(false);
      clearHideTimer();
    };

    const handleSelect = (value: GenderType) => {
      setSelectedValue(value);
      setDisplayValue(_.find(genderOptions, {selectedValue: value})?.displayValue);
      setHasValue(Boolean(String(value).trim()));
      onGenderChange?.(value);
    };

    const GenderButtons = (
      <div
        className={cn(
          "w-full h-full absolute flex gap-2 justify-end items-center p-2.5"
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {genderOptions.map((option) => (
          <button
            className="focus:outline-none"
            key={option.selectedValue}
            type="button"
            tabIndex={-1}
            onClick={() => handleSelect(option.selectedValue)}
            onBlur={onButtonBlur}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <i
              className={`fa-duotone fa-light ${option.icon} text-xl cursor-pointer transition-colors
                  ${
                selectedValue === option.selectedValue
                  ? option.activeColor
                  : `text-gray-400 ${option.hoverColor}`
              }`}
            />
          </button>
        ))}
      </div>
    )

    return (
      <div
        className="relative w-39">
        <Tippy
          onCreate={instance => register(instance)}
          onDestroy={instance => unregister(instance)}
          className="err-tooltip"
          placement="bottom"
          visible={!!error && showError}
          interactive={false}
          hideOnClick={true}
          delay={0}
          render={attrs => (
            <div
              {...attrs}
              tabIndex={-1}
              className="err-tooltip-box box bg-[#640a0a] text-white text-sm px-2 py-1 rounded shadow whitespace-pre-line"
            >
              {error ?? ""}
              <div
                data-popper-arrow
                className="err-tooltip-arrow bg-[#640a0a]! w-3! h-3! rotate-45!"
              />
            </div>
          )}
        >
          <Input
            ref={useMergeRefs(internalRef, ref, (props as any).ref)}
            className={
              cn(
                "absolute z-0",
                error ? "border-input-field__error" : "",
                className
              )
            }
            readOnly={true}
            placeholder={props.placeholder}
            value={displayValue}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            {...props}
          />
        </Tippy>

        {GenderButtons}

        {hasValue && (
          <Label className="absolute -top-2 left-2.25 text-[10px] font-semibold text-generic-dull">{props.placeholder}</Label>
        )}
      </div>
    );
  }
);

GenderRadio.displayName = "GenderRadio";
export {GenderRadio};